/**
 * Bátur theme features: page preloader, wishlist, header logo morph
 */
(function () {
  const STORAGE_KEY = 'batur-wishlist-v1';

  class PagePreloader {
    constructor(el) {
      this.el = el;
      this.minDuration = parseInt(el.dataset.minDuration || '1200', 10);
      this.startedAt = Date.now();
      this.done = false;

      if (document.readyState === 'complete') {
        this.hide();
      } else {
        window.addEventListener('load', () => this.hide(), { once: true });
        setTimeout(() => this.hide(), 8000);
      }
    }

    hide() {
      if (this.done) return;
      const elapsed = Date.now() - this.startedAt;
      const wait = Math.max(0, this.minDuration - elapsed);

      setTimeout(() => {
        this.done = true;
        this.el.classList.add('is-hidden');
        document.documentElement.classList.remove('is-preloading');
        setTimeout(() => this.el.remove(), 700);
      }, wait);
    }
  }

  class WishlistStore {
    static load() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : [];
      } catch {
        return [];
      }
    }

    static save(items) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { items } }));
    }

    static count() {
      return WishlistStore.load().length;
    }

    static has(variantId) {
      return WishlistStore.load().some((i) => String(i.variantId) === String(variantId));
    }

    static add(item) {
      const items = WishlistStore.load();
      if (!items.some((i) => String(i.variantId) === String(item.variantId))) {
        items.push({ ...item, addedAt: Date.now() });
        WishlistStore.save(items);
      }
    }

    static remove(variantId) {
      WishlistStore.save(WishlistStore.load().filter((i) => String(i.variantId) !== String(variantId)));
    }

    static toggle(item) {
      if (WishlistStore.has(item.variantId)) {
        WishlistStore.remove(item.variantId);
        return false;
      }
      WishlistStore.add(item);
      return true;
    }
  }

  function formatMoney(cents) {
    const settings = window.themeVariables?.settings || {};
    const format = settings.currencyCodeEnabled
      ? settings.moneyWithCurrencyFormat
      : settings.moneyFormat;
    const fmt = format || '€{{amount_with_comma_separator}}';

    const value = Number(cents) / 100;
    const amount = value.toFixed(2);
    const amountNoDecimals = String(Math.round(value));
    const amountComma = amount.replace('.', ',');
    const amountNoDecimalsComma = amountNoDecimals;

    return fmt
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountComma)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, amountNoDecimalsComma)
      .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, amountComma.replace(/,/g, "'"))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
      .replace(/\{\{\s*amount\s*\}\}/g, amount);
  }

  function updateWishlistCounts() {
    const count = WishlistStore.count();
    document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
      el.textContent = count > 0 ? `(${count})` : '';
      el.hidden = count === 0;
    });
    document.querySelectorAll('.wishlist-btn[data-variant-id]').forEach((btn) => {
      const active = WishlistStore.has(btn.dataset.variantId);
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  async function fetchProduct(handle) {
    const res = await fetch(`/products/${handle}.js`);
    if (!res.ok) throw new Error('Product not found');
    return res.json();
  }

  function getOptionIndex(product, name) {
    const n = name.toLowerCase();
    return product.options.findIndex((o) => o.toLowerCase() === n || o.toLowerCase().includes(n));
  }

  const wishlistModalState = { product: null, selectedVariant: null };

  function getWishlistModal() {
    return document.getElementById('wishlist-modal');
  }

  function confirmWishlistAdd() {
    const p = wishlistModalState.product;
    const v = wishlistModalState.selectedVariant;
    if (!p || !v) return;

    WishlistStore.add({
      handle: p.handle,
      productId: p.id,
      variantId: v.id,
      title: p.title,
      variantTitle: v.title,
      price: v.price,
      image: v.featured_image?.src || p.featured_image,
      url: `/products/${p.handle}?variant=${v.id}`,
    });
    updateWishlistCounts();
    getWishlistModal()?.hide();
  }

  function selectWishlistSize(size, sizeIdx, colorIdx) {
    const p = wishlistModalState.product;
    const v = p.variants.find((vr) => {
      if (vr.options[sizeIdx] !== size) return false;
      if (colorIdx >= 0) return vr.options[colorIdx] === wishlistModalState.selectedVariant.options[colorIdx];
      return true;
    });
    if (v) {
      wishlistModalState.selectedVariant = v;
      renderWishlistModal();
    }
  }

  function renderWishlistModal() {
    const p = wishlistModalState.product;
    const v = wishlistModalState.selectedVariant;
    const body = getWishlistModal()?.querySelector('[data-wishlist-modal-body]');
    if (!body || !p || !v) return;

    const sizeIdx = getOptionIndex(p, 'size');
    const colorIdx = getOptionIndex(p, 'color');

    let colorLabel = '';
    if (colorIdx >= 0) {
      colorLabel = `<p class="wishlist-modal__option-label">${v.options[colorIdx]}</p>`;
    }

    const thumbs = p.images
      .slice(0, 8)
      .map(
        (src, i) =>
          `<button type="button" class="wishlist-modal__thumb${i === 0 ? ' is-selected' : ''}"><img src="${src}" alt="" width="72" height="72" loading="lazy"></button>`
      )
      .join('');

    let sizesHtml = '';
    if (sizeIdx >= 0) {
      const sizes = [...new Set(p.variants.map((vr) => vr.options[sizeIdx]))];
      sizesHtml = `<p class="wishlist-modal__option-label">${window.themeVariables?.strings?.wishlistSize || 'Size'}</p><div class="wishlist-modal__sizes">`;
      sizes.forEach((size) => {
        const match = p.variants.find(
          (vr) => vr.options[sizeIdx] === size && (colorIdx < 0 || vr.options[colorIdx] === v.options[colorIdx])
        );
        const selected = v.options[sizeIdx] === size;
        const disabled = !match?.available;
        sizesHtml += `<button type="button" class="wishlist-modal__size${selected ? ' is-selected' : ''}" data-size="${size}" ${disabled ? 'disabled' : ''}>${size}</button>`;
      });
      sizesHtml += '</div>';
    }

    body.innerHTML = `
      <div class="wishlist-modal__product-row">
        <p class="h6">${p.title}</p>
        <p class="h6">${formatMoney(v.price)}</p>
      </div>
      <div class="wishlist-modal__thumbs">${thumbs}</div>
      ${colorLabel}
      ${sizesHtml}
      <button type="button" class="button button--xl w-full" data-wishlist-add style="--button-background: rgb(var(--text-color)); --button-text-color: rgb(var(--background));">
        ${window.themeVariables?.strings?.wishlistAdd || 'Add to wishlist'}
      </button>
    `;

    body.querySelectorAll('[data-size]').forEach((btn) => {
      btn.addEventListener('click', () => selectWishlistSize(btn.dataset.size, sizeIdx, colorIdx));
    });
    body.querySelector('[data-wishlist-add]')?.addEventListener('click', confirmWishlistAdd);
  }

  async function openWishlistModal(handle, defaultVariantId) {
    const modal = getWishlistModal();
    if (!modal) return;

    wishlistModalState.product = await fetchProduct(handle);
    wishlistModalState.selectedVariant =
      wishlistModalState.product.variants.find((vr) => vr.id === Number(defaultVariantId)) ||
      wishlistModalState.product.variants.find((vr) => vr.available) ||
      wishlistModalState.product.variants[0];

    renderWishlistModal();
    modal.show();
  }

  function productNeedsSizeModal(product) {
    if (product.variants.length <= 1) return false;
    return getOptionIndex(product, 'size') >= 0;
  }

  function syncWishlistVariantFromCard(card) {
    const btn = card?.querySelector?.('.wishlist-btn[data-variant-id]');
    if (!btn) return;

    const selected = card.querySelector('.product-card__swatch-list [type="radio"]:checked');
    const variantId = selected?.getAttribute('data-variant-id');
    if (variantId) btn.dataset.variantId = variantId;
  }

  function bindWishlistButtons(root = document) {
    root.querySelectorAll('.wishlist-btn[data-product-handle]:not([data-wishlist-bound])').forEach((btn) => {
      btn.setAttribute('data-wishlist-bound', '');
      btn.addEventListener(
        'click',
        (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (btn.classList.contains('is-loading')) return;
          handleWishlistClick(btn);
        },
        true
      );
    });
  }

  async function handleWishlistClick(btn) {
    const handle = btn.dataset.productHandle;
    const variantId = btn.dataset.variantId;
    if (!handle || btn.classList.contains('is-loading')) return;

    btn.classList.add('is-loading');

    let product;
    try {
      product = await fetchProduct(handle);
    } catch {
      btn.classList.remove('is-loading');
      return;
    }

    if (productNeedsSizeModal(product)) {
      btn.classList.remove('is-loading');
      openWishlistModal(handle, variantId);
      return;
    }

    const v = product.variants.find((vr) => String(vr.id) === String(variantId)) || product.variants[0];
    const added = WishlistStore.toggle({
      handle: product.handle,
      productId: product.id,
      variantId: v.id,
      title: product.title,
      variantTitle: v.title,
      price: v.price,
      image: v.featured_image?.src || product.featured_image,
      url: `/products/${product.handle}?variant=${v.id}`,
    });
    btn.classList.toggle('is-active', added);
    btn.setAttribute('aria-pressed', added ? 'true' : 'false');
    btn.classList.remove('is-loading');
    updateWishlistCounts();
  }

  async function fetchWishlistProductCard(handle, variantId) {
    const url = `/products/${encodeURIComponent(handle)}?variant=${variantId}&section_id=wishlist-product-item`;
    const res = await fetch(url);
    if (!res.ok) return null;

    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.querySelector('.wishlist-page__item');
  }

  async function renderWishlistPage() {
    const root = document.querySelector('[data-wishlist-page]');
    if (!root) return;

    const grid = root.querySelector('[data-wishlist-grid]');
    const empty = root.querySelector('[data-wishlist-empty]');
    const countEl = root.querySelector('[data-wishlist-page-count]');
    const items = WishlistStore.load();

    if (countEl) countEl.textContent = `(${items.length})`;

    if (!items.length) {
      if (grid) grid.hidden = true;
      if (empty) empty.hidden = false;
      return;
    }

    if (empty) empty.hidden = true;
    if (!grid) return;

    grid.hidden = false;
    grid.innerHTML = '';

    const cards = await Promise.all(
      items.map((item) => fetchWishlistProductCard(item.handle, item.variantId))
    );

    cards.filter(Boolean).forEach((card) => grid.appendChild(card));
    bindWishlistButtons(grid);
    updateWishlistCounts();
  }

  function initLogoMorph() {
    const morph = document.querySelector('[data-header-logo-morph]');
    const tracker = document.getElementById('header-scroll-tracker');
    if (!morph || !tracker) return;

    const observer = new IntersectionObserver(
      ([entry]) => morph.classList.toggle('is-scrolled', !entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(tracker);
  }

  function initEditorialProductCards() {
    document.querySelectorAll('.collection-editorial product-card[reveal-on-scroll]').forEach((card) => {
      card.removeAttribute('reveal-on-scroll');
      card.style.opacity = '1';
    });
  }

  /** Ensure slideshow / hero videos preload and play after theme carousel init. */
  function initHighQualityVideos() {
    document.querySelectorAll('video[data-batur-hq-video]').forEach((video) => {
      video.setAttribute('preload', 'auto');
    });

    document.querySelectorAll('slideshow-carousel').forEach((carousel) => {
      const slide =
        carousel.querySelector('.slideshow__slide.is-selected[data-slide-type="video"]') ||
        carousel.querySelector('.slideshow__slide[data-slide-type="video"]');

      if (!slide) return;

      slide.querySelectorAll('video-media').forEach((media) => {
        customElements.upgrade(media);
        media.play({ restart: true }).catch(() => {});
      });
    });
  }

  function init() {
    const preloader = document.getElementById('page-preloader');
    if (preloader) new PagePreloader(preloader);

    updateWishlistCounts();
    bindWishlistButtons();
    document.querySelectorAll('product-card').forEach(syncWishlistVariantFromCard);
    renderWishlistPage();
    initLogoMorph();
    initEditorialProductCards();
    initHighQualityVideos();
  }

  document.addEventListener('change', (e) => {
    const radio = e.target.closest?.('.product-card__swatch-list [type="radio"]');
    if (!radio) return;
    syncWishlistVariantFromCard(radio.closest('product-card'));
  });

  document.addEventListener('click', (e) => {
    const removeId = e.target.closest('[data-remove-wishlist]')?.dataset.removeWishlist;
    if (removeId) {
      e.preventDefault();
      WishlistStore.remove(removeId);
      renderWishlistPage();
      updateWishlistCounts();
    }
  });

  document.addEventListener('wishlist:updated', () => {
    updateWishlistCounts();
    renderWishlistPage();
  });

  document.addEventListener(
    'carousel:change',
    (e) => {
      const carousel = e.target;
      if (!carousel?.matches?.('slideshow-carousel')) return;

      const cell = e.detail?.cell;
      if (cell?.getAttribute('data-slide-type') !== 'video') return;

      cell.querySelectorAll('video-media').forEach((media) => {
        customElements.upgrade(media);
        media.play({ restart: true }).catch(() => {});
      });
    },
    true
  );

  document.documentElement.addEventListener('theme:loading:end', () => {
    initEditorialProductCards();
    initHighQualityVideos();
    bindWishlistButtons();
    document.querySelectorAll('product-card').forEach(syncWishlistVariantFromCard);
    renderWishlistPage();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
